/**
 * TS bridge to the custom Android `DeviceFlags` Capacitor plugin.
 *
 * The native Java implementation lives at
 * `capacitor-plugins/device-flags/android/...` and is auto-registered by
 * Capacitor on `npx cap sync android` (declared as a local file: dependency
 * in root package.json — that's what wires up the native side).
 *
 * Web/iOS bundling: we register the plugin name directly here via
 * `@capacitor/core` so Vite never has to resolve the local plugin's JS
 * entry point (avoids file:-dep ESM resolver edge cases). Native Android
 * registration is fully independent and unaffected.
 */
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

export interface LocationStateChangedEvent {
  enabled: boolean;
}

export interface DeviceFlagsPlugin {
  /**
   * Reads low-level Android device flags.
   * - developerMode: whether "Developer options" is enabled in Settings
   * - adbEnabled:    whether USB debugging (ADB) is enabled
   *
   * On non-Android platforms this plugin is not implemented; callers should
   * guard with `Capacitor.getPlatform() === 'android'` before invoking.
   */
  getFlags(): Promise<{ developerMode: boolean; adbEnabled: boolean }>;

  /**
   * Reads the current system Location toggle state. Android-only.
   */
  isLocationEnabled(): Promise<{ enabled: boolean }>;

  /**
   * Opens the system Location settings screen. Android-only.
   */
  openLocationSettings(): Promise<void>;

  /** Whether the app is whitelisted from battery optimisation. Android-only. */
  isIgnoringBatteryOptimizations(): Promise<{ enabled: boolean }>;
  /** Opens the request-ignore-battery-optimisations system dialog. */
  requestIgnoreBatteryOptimizations(): Promise<void>;

  /** Whether the app can draw over other apps (SYSTEM_ALERT_WINDOW). Android-only. */
  canDrawOverlays(): Promise<{ enabled: boolean }>;
  /** Opens the per-app "Display over other apps" settings page. */
  openOverlaySettings(): Promise<void>;

  /** Opens the system Developer Options settings screen. */
  openDeveloperSettings(): Promise<void>;

  /** Whether ACCESS_BACKGROUND_LOCATION is granted. Android-only. Pre-Q always true. */
  hasBackgroundLocationPermission(): Promise<{ enabled: boolean }>;
  /** Opens this app's "App info" settings page (Location → Allow all the time). */
  openAppLocationSettings(): Promise<void>;
  /** Triggers native ACCESS_BACKGROUND_LOCATION runtime prompt (Android 11+ routes to system Location screen). */
  requestBackgroundLocationPermission(): Promise<void>;

  /**
   * Opens the system App Notification Settings for this app. Android-only.
   * Used as a fallback when POST_NOTIFICATIONS has been permanently denied
   * and the runtime permission dialog will no longer appear.
   */
  openAppNotificationSettings(): Promise<void>;

  /**
   * Returns whether the app is currently allowed to launch a full-screen
   * notification intent (required for the trip-offer popup over the lock
   * screen on Android 14+). Always resolves true on Android < 14.
   */
  canUseFullScreenIntent(): Promise<{ allowed: boolean }>;

  /**
   * Opens the system "Full screen notifications" settings screen for this
   * app on Android 14+, falling back to the app notification settings page
   * on earlier versions.
   */
  openFullScreenIntentSettings(): Promise<void>;

  /** Whether the OS is an OEM that requires a vendor Autostart toggle for FCM to survive swipe-from-recents. */
  isOemAutoStartRequired(): Promise<{ required: boolean }>;
  isOemAutoStartConfirmed(): Promise<{ confirmed: boolean }>;
  markOemAutoStartConfirmed(): Promise<void>;
  openOemAutoStartSettings(): Promise<void>;

  /**
   * Real-time event fired when the system Location toggle changes.
   * Backed by Android's PROVIDERS_CHANGED_ACTION broadcast.
   */
  addListener(
    eventName: 'locationStateChanged',
    listenerFunc: (event: LocationStateChangedEvent) => void
  ): Promise<PluginListenerHandle>;
}

export const DeviceFlags = registerPlugin<DeviceFlagsPlugin>('DeviceFlags');
